import { BaseRepository } from '../BaseRepository';
import { BFF_ENDPOINTS, type BffEndpoint } from '../../endpoints/BffEndpoints';
import { BffAuthenticatedApiClient } from '../../clients/BffAuthenticatedApiClient';
import type { HttpResponse } from '../../http/HttpClient';
import type { BillingCalendarResponse } from '../../models/bff/Billing';
import type { BalanceResponse } from '../../models/bff/Invoice';
import type {
  RawReceiptsListResponse,
  RawReceiptResponse,
  ReceiptsListResponse,
  ReceiptResponse,
} from '../../models/bff/Receipt';
import type { PaginationQueryParams } from '../../models/bff/Common';
import { filterQueryParams } from '../../../utils/queryParams';
import { normalizeReceipt } from '../../../operations/receiptNormalizer';

const NAME = 'billing';

/**
 * Repository for billing-related operations.
 * Handles communication with the BFF for billing data.
 */
export class BillingRepository extends BaseRepository<BffEndpoint> {
  static override readonly name = NAME;

  constructor() {
    super(BffAuthenticatedApiClient.getInstance());
  }

  /**
   * Trigger a payment retry for all unpaid payments of the authenticated
   * subscriber's billing account.
   *
   * @returns Promise that resolves when the retry has been triggered (204 No Content)
   */
  retryPayment(): Promise<HttpResponse<void>> {
    return this.apiClient.post<void>(BFF_ENDPOINTS.BILLING_PAYMENT_RETRIES);
  }

  /**
   * Get the outstanding balance and unpaid invoices for the authenticated subscriber.
   * @returns Outstanding balance with unpaid invoices
   */
  getBalance(): Promise<HttpResponse<BalanceResponse>> {
    return this.apiClient.get<BalanceResponse>(BFF_ENDPOINTS.BALANCE);
  }


  /**
   * Get the paginated list of receipts for the authenticated subscriber.
   * @param queryParams - Optional pagination parameters (page, size)
   * @returns Paginated list of receipts
   */
  getReceipts(queryParams?: PaginationQueryParams): Promise<HttpResponse<ReceiptsListResponse>> {
    const filteredParams = filterQueryParams(queryParams);

    return this.apiClient.get<RawReceiptsListResponse>(
      BFF_ENDPOINTS.RECEIPTS,
      {
        query: filteredParams,
      },
    ).then(response => ({
      ...response,
      data: { ...response.data, content: response.data.content.map(normalizeReceipt) },
    }));
  }

  /**
   * Get billing calendar information for a given product pricing code.
   * Returns deferred start dates and renewal dates used during subscription registration.
   *
   * @param productPricingCode - The plan code (e.g., 'VEL-BASIC-SOLO-OW1-MONTHLY-PLAN')
   * @returns Promise with billing calendar response
   */
  getBillingCalendar(productPricingCode: string): Promise<HttpResponse<BillingCalendarResponse>> {
    return this.apiClient.get<BillingCalendarResponse>(
      BFF_ENDPOINTS.BILLING_CALENDAR,
      {
        query: { product_pricing_code: productPricingCode },
      },
    );
  }

  /**
   * Get a receipt by ID.
   *
   * @param receiptId - The receipt ID
   * @returns Promise with the receipt response
   */
  getReceipt(receiptId: string): Promise<HttpResponse<ReceiptResponse>> {
    return this.apiClient.get<RawReceiptResponse>(
      BFF_ENDPOINTS.RECEIPT,
      {
        pathParams: { receiptId },
      },
    ).then(response => ({
      ...response,
      data: normalizeReceipt(response.data),
    }));
  }
}

declare module '../Repositories' {
  export interface Repositories {
    [NAME]: BillingRepository;
  }
}
